import React, { useEffect, useMemo, useState } from 'react';
import { findLastIndex } from 'lodash';

import Button from '../../library/button/button';
import Image from '../../library/image/image';
import Typography from '../../library/typography/typography';
import {
  ChevronLeftRegularIcon,
  PlusRegularIcon
} from '../../shared/icons/icons';

import usePlpProducts from '../../pages/product-list-page/hooks/use-plp-products';
import {
  useCompareProducts,
  useDataLayer,
  useFitment,
  useProductQuantity
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import { ProductData, ProductTypeEnum } from '../../shared/types/graphql-types';

import styles from './product-compare-oe-card.scss';

interface ProductCompareOeCardProps {
  currentIndex: number;
  isCompareDrawerFlow?: boolean;
  isFitmentSearch?: boolean;
  isPackageSearch?: boolean;
  isSizeSearch?: boolean;
  isStaggeredSearch?: boolean;
  onAddToCompare?: (trackActionId?: string) => void;
  productType?: string;
  queryType?: string;
}

function ProductCompareOeCard(props: ProductCompareOeCardProps) {
  const {
    currentIndex,
    isCompareDrawerFlow = false,
    isFitmentSearch = false,
    isPackageSearch = false,
    isSizeSearch = false,
    isStaggeredSearch = false,
    onAddToCompare,
    productType,
    queryType
  } = props;

  const [showMultipleOeOptions, setShowMultipleOeOptions] = useState(false);
  const { currentPageType } = useDataLayer();
  const { onProductComparison, productsForComparison } = useCompareProducts();
  const { isFitmentLoading, selectedFitment } = useFitment();

  const { fetchOEProducts, oeProductsData } = usePlpProducts({
    isFitmentLoading,
    isFitmentSearch,
    isPackageSearch,
    isSizeSearch,
    isStaggeredSearch,
    productType,
    queryType,
    selectedFitment
  });
  const { isOE, make } = selectedFitment || {};

  const analyticsPrefix = `${currentPageType}${
    isCompareDrawerFlow ? '_compare_drawer' : ''
  }`;
  const normalizedProductType = ProductTypeEnum.Tires;
  const { quantity, rearQuantity } = useProductQuantity({
    productType: normalizedProductType
  });

  const filteredOeProducts = useMemo(
    () =>
      oeProductsData?.filter(
        (oeProduct: ProductData) =>
          !productsForComparison.some(
            product => product.code === oeProduct?.code
          )
      ),
    [oeProductsData, productsForComparison]
  );

  const lastFilledIndex = useMemo(
    () =>
      findLastIndex(
        productsForComparison,
        item => item && item?.code?.trim() !== ''
      ),
    [productsForComparison]
  );

  const showOeLink = useMemo(() => {
    const selectedIndex = isCompareDrawerFlow
      ? currentIndex === lastFilledIndex - 1
      : currentIndex === lastFilledIndex + 1;
    return (
      (selectedIndex || currentIndex === 0) &&
      isOE &&
      isArrayWithLength(filteredOeProducts)
    );
  }, [
    currentIndex,
    filteredOeProducts,
    isCompareDrawerFlow,
    isOE,
    lastFilledIndex
  ]);

  function handleOnClick(product) {
    const isSelectedForComparison =
      product &&
      productsForComparison?.some(
        p =>
          p?.code === product?.code &&
          p?.rearProduct?.code === product?.rearProduct?.code
      );
    onProductComparison(
      product,
      !isSelectedForComparison,
      quantity,
      rearQuantity
    );
    setShowMultipleOeOptions(false);
  }

  useEffect(() => {
    if (filteredOeProducts?.length <= 0 && isOE) {
      fetchOEProducts();
    }
  }, [fetchOEProducts, isOE, filteredOeProducts]);

  return (
    <>
      {showOeLink ? (
        <>
          {showMultipleOeOptions ? (
            <div
              className={
                isCompareDrawerFlow
                  ? styles['container-drawer']
                  : styles['container']
              }
            >
              <div
                className={
                  isCompareDrawerFlow ? styles['container-with-angle-bg'] : ''
                }
              >
                {isCompareDrawerFlow && (
                  <Image
                    alt="icon-angle"
                    className={styles['icon-angle']}
                    src="https://discounttire.scene7.com/is/content/discounttire/logos/backgrounds/card_light_gray_curve.svg"
                  />
                )}
                <div
                  className={
                    isCompareDrawerFlow
                      ? styles['oe-option-title-drawer']
                      : styles['oe-option-title']
                  }
                >
                  <ChevronLeftRegularIcon
                    className={styles['back-icon']}
                    onClick={() =>
                      setShowMultipleOeOptions(!showMultipleOeOptions)
                    }
                  />
                  {isCompareDrawerFlow && (
                    <Typography className={styles['title']} tag="div">
                      Select your original {make} tire to compare
                    </Typography>
                  )}
                </div>
              </div>

              <div
                className={
                  isCompareDrawerFlow
                    ? styles['drawer-option-container']
                    : styles['option-container']
                }
              >
                {filteredOeProducts.map((product: ProductData) => (
                  <Typography
                    className={
                      isCompareDrawerFlow
                        ? styles['oe-option-drawer']
                        : styles['oe-option']
                    }
                    key={product.code}
                    onClick={() => {
                      setShowMultipleOeOptions(prev => !prev);
                      handleOnClick(product);
                    }}
                    tag="div"
                  >
                    <Typography
                      className={styles['brand']}
                      size="extraSmall"
                      tag="div"
                    >
                      {product?.brand}
                    </Typography>
                    <Typography
                      className={styles['name']}
                      tag="div"
                      weight="bold"
                    >
                      {product?.name}
                    </Typography>
                  </Typography>
                ))}
              </div>
            </div>
          ) : (
            <Typography
              aria-label="Add oe tire"
              className={styles['placeholder-image']}
              size="small"
              tag="div"
            >
              <div className={styles['link-container']}>
                {' '}
                <PlusRegularIcon className={styles['plus-icon']} />{' '}
                <Button
                  className={styles['oe-link']}
                  onClick={() => {
                    const showMultiple =
                      filteredOeProducts?.length > 1 && !showMultipleOeOptions;

                    if (showMultiple) {
                      setShowMultipleOeOptions(prev => !prev);
                    } else {
                      handleOnClick(filteredOeProducts[0]);
                    }

                    trackAction(
                      `${analyticsPrefix}_add_oem${
                        showMultiple ? '_multiple' : ''
                      }`
                    );
                  }}
                  size="regular"
                >
                  Add your original {make} tire
                </Button>
              </div>
            </Typography>
          )}
        </>
      ) : (
        <>
          {isCompareDrawerFlow ? (
            <div className={styles['add-tire-container']}>
              <Button
                className={styles['add-tire-btn']}
                onClick={() => {
                  onAddToCompare?.(`${analyticsPrefix}_add_from_plp`);
                }}
              >
                <div>
                  <PlusRegularIcon />
                  <Typography className={styles['add-tire-title']}>
                    {' '}
                    Add to compare
                  </Typography>
                </div>
              </Button>
            </div>
          ) : (
            <Typography
              aria-label="Add to compare"
              className={styles['placeholder-image']}
              size="small"
              tag="div"
            >
              <PlusRegularIcon className={styles['plus-icon']} /> Add to compare
            </Typography>
          )}
        </>
      )}
    </>
  );
}

export default ProductCompareOeCard;
